import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

export interface ExtensionStatus {
  /** The version running in this browser, or null when the extension is not installed (or older than 0.3.1). */
  installed: string | null
  /** The newest version Tapwork is serving, or null while it loads. */
  latest: string | null
  /** An extension older than 0.3.1 is installed: it cannot say which version, but it answers a probe. */
  legacy: boolean
}

/**
 * Versions before 0.3.1 do not stamp <html>, but they do answer the handoff request — with an
 * error for an id they do not know. That reply is proof they are installed.
 */
function useLegacyExtensionProbe(enabled: boolean): boolean {
  const [found, setFound] = useState(false)
  useEffect(() => {
    if (!enabled) return
    const probeId = crypto.randomUUID()
    const onMessage = (event: MessageEvent<unknown>) => {
      if (event.source !== window || event.origin !== window.location.origin) return
      const data = event.data as { type?: string; handoffId?: string } | null
      if (data?.handoffId === probeId && data.type === 'TAPWORK_GMAIL_HANDOFF_ERROR') setFound(true)
    }
    window.addEventListener('message', onMessage)
    window.postMessage({ type: 'TAPWORK_GMAIL_HANDOFF_REQUEST', handoffId: probeId }, window.location.origin)
    return () => window.removeEventListener('message', onMessage)
  }, [enabled])
  return found
}

export function versionAtLeast(version: string, minimum: string): boolean {
  const a = version.split('.').map((part) => Number.parseInt(part, 10) || 0)
  const b = minimum.split('.').map((part) => Number.parseInt(part, 10) || 0)
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0)
    if (diff !== 0) return diff > 0
  }
  return true
}

/** The extension stamps its version on <html> before the app starts. */
export function installedExtensionVersion(): string | null {
  return document.documentElement.dataset.tapworkExtension || null
}

export function useExtensionStatus(): ExtensionStatus {
  const latest = useQuery({
    queryKey: ['extension-latest-version'],
    queryFn: async (): Promise<string | null> => {
      const response = await fetch('/downloads/tapwork-chrome-extension.json', { cache: 'no-store' })
      if (!response.ok) return null
      const data = (await response.json()) as { version?: string }
      return typeof data.version === 'string' && /^\d+(\.\d+){0,3}$/.test(data.version) ? data.version : null
    },
    staleTime: 10 * 60_000,
    retry: false,
  })
  const installed = installedExtensionVersion()
  const legacy = useLegacyExtensionProbe(installed === null)
  return { installed, latest: latest.data ?? null, legacy }
}
